import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Check, Crown, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMessage } from "../lib/api";
import { track } from "../lib/analytics";

const PREMIUM_FEATURES = [
  "Full speech AND math for the whole family",
  "Socratic tutor, placement, warmups & mini-games",
  "Opportunities hub & weekly parent digest",
];
const PRO_FEATURES = [
  "Unlimited child profiles for your caseload",
  "Branded progress report exporting (PDF + email)",
  "Everything in Premium for every profile",
  "Priority support",
];

// Dark-mode pricing for the cinematic landing page. Monthly/Annual toggle on
// Premium, plus the Professional (SLP) tier. One card-backed 3-day free trial
// per account — payment method collected at checkout, auto-bills at trial end.
export const PricingSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [annual, setAnnual] = useState(true);
  const [plans, setPlans] = useState(null);
  const [starting, setStarting] = useState("");
  // "Applied" hint after the visitor clicks the BETA50 ribbon.
  const [beta50, setBeta50] = useState(() => localStorage.getItem("applyBeta50") === "1");
  useEffect(() => {
    const on = () => setBeta50(true);
    window.addEventListener("beta50-applied", on);
    return () => window.removeEventListener("beta50-applied", on);
  }, []);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    track("view_pricing");
  }, []);

  const premiumKey = annual ? "premium_annual" : "premium_monthly";
  const plan = (key) => (plans || []).find((p) => p.lookup_key === key);

  const startCheckout = async (lookupKey) => {
    if (!user) { navigate(`/auth?mode=register&plan=${lookupKey}`); return; }
    setStarting(lookupKey);
    track("begin_subscription", { plan: lookupKey });
    // GA4 recommended ecommerce event — mark as a key event for funnels
    track("begin_checkout", { currency: "usd",
      items: [{ item_id: lookupKey }] });
    try {
      // Banner auto-apply: BETA50 is pre-filled when the visitor clicked the ribbon.
      const promo = localStorage.getItem("applyBeta50") === "1" ? "BETA50" : undefined;
      const { data } = await api.post("/payments/checkout",
        { lookup_key: lookupKey, origin_url: window.location.origin, ...(promo ? { promo } : {}) });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMessage(e)); setStarting(""); }
  };

  return (
    <section className="bg-[#070B14] py-24 pb-12 px-6" id="pricing" data-testid="pricing-section">
      <div className="max-w-4xl mx-auto space-y-10">
        <div className="text-left space-y-3">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-emerald-400">Pricing</p>
          <h2 className="text-base md:text-lg font-bold text-white/70">3-day free trial — payment method required</h2>
          {beta50 && (
            <p className="inline-block rounded-full bg-amber-400/15 border border-amber-400/40 px-4 py-1.5 text-sm font-bold text-amber-300"
              data-testid="beta50-applied-hint">
              BETA50 applied — 50% off your first month at checkout
            </p>
          )}
          <div className="flex items-center gap-3" data-testid="pricing-interval-toggle">
            <button type="button" onClick={() => setAnnual(false)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${
                !annual ? "bg-emerald-400 text-slate-950" : "text-slate-400 border border-slate-700 hover:text-white"}`}
              data-testid="pricing-toggle-monthly">Monthly</button>
            <button type="button" onClick={() => setAnnual(true)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${
                annual ? "bg-emerald-400 text-slate-950" : "text-slate-400 border border-slate-700 hover:text-white"}`}
              data-testid="pricing-toggle-annual">
              Annual <span className={annual ? "text-amber-700" : "text-amber-400"}> · SAVE 45%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative rounded-3xl border-2 border-emerald-400/60 bg-slate-900/70 backdrop-blur p-8 space-y-5"
            data-testid="pricing-premium-card">
            {annual && (
              <span className="absolute -top-3 right-6 rounded-full bg-amber-400 text-slate-950 text-xs font-extrabold px-3 py-1"
                data-testid="pricing-save-badge">SAVE 45%</span>
            )}
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-emerald-400" aria-hidden="true" />
              <h3 className="text-lg font-bold text-white">Premium</h3>
            </div>
            <p className="text-4xl font-extrabold text-white" data-testid="pricing-premium-price">
              {annual ? "$99.99" : "$14.99"}
              <span className="text-base font-semibold text-slate-400">{annual ? " / year" : " / month"}</span>
            </p>
            {annual && <p className="text-xs text-emerald-300 font-semibold">That's $8.33 / month — vs $179.88 billed monthly</p>}
            <ul className="space-y-2.5">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="text-sm text-slate-300 flex gap-2">
                  <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" aria-hidden="true" /> {f}
                </li>
              ))}
            </ul>
            <button type="button" disabled={Boolean(starting)}
              onClick={() => startCheckout(premiumKey)}
              className="w-full h-12 rounded-full bg-emerald-400 text-slate-950 font-extrabold hover:bg-emerald-300 transition-colors disabled:opacity-60"
              data-testid="pricing-premium-cta">
              {starting === premiumKey ? "Opening checkout…" : "Start 3-Day Free Trial"}
            </button>
            <p className="text-[11px] text-slate-500">
              $0 today, card required · billing starts automatically when your 3-day trial ends — cancel before then and you won't be charged
            </p>
          </div>

          <div className="rounded-3xl border border-slate-700 bg-slate-900/50 backdrop-blur p-8 space-y-5"
            data-testid="pricing-professional-card">
            <div className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-sky-400" aria-hidden="true" />
              <h3 className="text-lg font-bold text-white">Professional</h3>
              <span className="rounded-full border border-sky-400/50 text-sky-300 text-[10px] font-bold px-2 py-0.5">
                For SLPs &amp; teachers
              </span>
            </div>
            <p className="text-4xl font-extrabold text-white" data-testid="pricing-professional-price">
              $29.99<span className="text-base font-semibold text-slate-400"> / month</span>
            </p>
            <ul className="space-y-2.5">
              {(plan("professional_monthly")?.features || PRO_FEATURES).map((f) => (
                <li key={f} className="text-sm text-slate-300 flex gap-2">
                  <BadgeCheck className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" aria-hidden="true" /> {f}
                </li>
              ))}
            </ul>
            <button type="button" disabled={Boolean(starting)}
              onClick={() => startCheckout("professional_monthly")}
              className="w-full h-12 rounded-full border-2 border-sky-400 text-sky-300 font-extrabold hover:bg-sky-400/10 transition-colors disabled:opacity-60"
              data-testid="pricing-professional-cta">
              {starting === "professional_monthly" ? "Opening checkout…" : "Start 3-Day Free Trial"}
            </button>
            <p className="text-[11px] text-slate-500">
              $0 today, card required · bills automatically when the 3-day trial ends · unlimited caseload
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 max-w-xl mx-auto pb-12"
          data-testid="pricing-disclaimer">
          {/* Stripe wordmark — trust signal for parents at checkout */}
          <svg viewBox="0 0 60 25" className="h-5 w-12 shrink-0" role="img"
            aria-label="Stripe" data-testid="pricing-stripe-logo">
            <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z" />
          </svg>
          <p className="text-xs text-[#94A3B8] text-left">
            Payments processed securely by Stripe. Every child stays protected by your per-child
            consent choices and privacy controls.
          </p>
        </div>
      </div>
    </section>
  );
};
